/**
 * CircuitKindBadge — labels circuit *deviations* from the canonical
 * `main` PCB. Renders nothing for the default kind; only `candidate`,
 * `fixture`, and `scratch` get a chip.
 *
 *   * candidate — dashed amber border, ⤴ icon  (a branch under review)
 *   * fixture   — solid grey border, 🔧 icon    (test rig, ships separately)
 *   * scratch   — faded amber background, ✎ icon (ephemeral chat draft)
 *
 * Used in Project cards, the Instrument Rack project segment, the
 * Circuits tab, and version timelines.
 */
import BranchIcon from "@mui/icons-material/CallSplitRounded";
import ScratchIcon from "@mui/icons-material/EditNoteRounded";
import FixtureIcon from "@mui/icons-material/HandymanRounded";
import { Chip, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";

type Kind = "main" | "candidate" | "fixture" | "scratch";

interface CircuitKindBadgeProps {
  kind: Kind | undefined | null;
  size?: "small" | "medium";
}

const KIND_LABEL: Record<Exclude<Kind, "main">, string> = {
  candidate: "Candidate",
  fixture: "Fixture",
  scratch: "Scratch",
};

export function CircuitKindBadge({
  kind,
  size = "small",
}: CircuitKindBadgeProps) {
  if (kind == null || kind === "main") return null;

  const KindIcon =
    kind === "candidate"
      ? BranchIcon
      : kind === "fixture"
        ? FixtureIcon
        : ScratchIcon;

  return (
    <Tooltip title={KIND_LABEL[kind]}>
      <Chip
        size={size}
        icon={<KindIcon fontSize="small" />}
        label={KIND_LABEL[kind]}
        sx={(t) => {
          const accent =
            kind === "fixture"
              ? t.palette.text.disabled
              : t.palette.editorial.main;
          return {
            fontWeight: 600,
            borderStyle: kind === "candidate" ? "dashed" : "solid",
            borderWidth: 1,
            borderColor: accent,
            color: accent,
            bgcolor: kind === "scratch" ? alpha(accent, 0.08) : "transparent",
            "& .MuiChip-icon": { color: accent },
          };
        }}
      />
    </Tooltip>
  );
}
